import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Logger from 'utils/logger';

const RENDER_READY_TIMEOUT_MS = 20_000;

interface ReportPreviewRenderReadyContextValue {
  registerBlock: (blockId: string) => void;
  unregisterBlock: (blockId: string) => void;
  markBlockReady: (blockId: string) => void;
  isReady: boolean;
}

const ReportPreviewRenderReadyContext =
  createContext<ReportPreviewRenderReadyContextValue | null>(null);

export function signalReportPreviewReady(): void {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  document.body.classList.add('ready');
  Object.assign(window, { reportsReadyToScreenshot: true });
}

export function resetReportPreviewReadySignal(): void {
  document.body.classList.remove('ready');
  Object.assign(window, { reportsReadyToScreenshot: false });
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
}

export function areAllReportPreviewBlocksReady(
  blocks: Map<string, boolean>,
): boolean {
  if (blocks.size === 0) {
    return true;
  }

  return Array.from(blocks.values()).every(Boolean);
}

interface ProviderProps {
  children: ReactNode;
  resetKey?: string | number;
}

export const ReportPreviewRenderReadyProvider: FC<ProviderProps> = ({
  children,
  resetKey,
}) => {
  const blocksRef = useRef<Map<string, boolean>>(new Map());
  const registrationClosedRef = useRef(false);
  const finalizeStartedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const tryFinalize = useCallback(async () => {
    if (finalizeStartedRef.current || !registrationClosedRef.current) {
      return;
    }

    if (!areAllReportPreviewBlocksReady(blocksRef.current)) {
      return;
    }

    finalizeStartedRef.current = true;

    try {
      await document.fonts.ready;
      await waitForNextPaint();
      setIsReady(true);
    } catch (error) {
      Logger.warn('Report preview render ready finalize failed', error);
      setIsReady(true);
    }
  }, []);

  const registerBlock = useCallback((blockId: string) => {
    blocksRef.current.set(blockId, false);

    if (finalizeStartedRef.current) {
      finalizeStartedRef.current = false;
      setIsReady(false);
      resetReportPreviewReadySignal();
    }
  }, []);

  const unregisterBlock = useCallback(
    (blockId: string) => {
      blocksRef.current.delete(blockId);
      void tryFinalize();
    },
    [tryFinalize],
  );

  const markBlockReady = useCallback(
    (blockId: string) => {
      if (!blocksRef.current.has(blockId)) {
        return;
      }

      blocksRef.current.set(blockId, true);
      void tryFinalize();
    },
    [tryFinalize],
  );

  useLayoutEffect(() => {
    registrationClosedRef.current = false;
    finalizeStartedRef.current = false;
    setIsReady(false);
    resetReportPreviewReadySignal();

    const blocks = blocksRef.current;
    const frameId = requestAnimationFrame(() => {
      registrationClosedRef.current = true;
      void tryFinalize();
    });

    return () => {
      cancelAnimationFrame(frameId);
      blocks.clear();
    };
  }, [resetKey, tryFinalize]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (finalizeStartedRef.current) {
        return;
      }

      Logger.warn(
        'Report preview render ready timed out before all charts rendered',
      );
      finalizeStartedRef.current = true;
      setIsReady(true);
    }, RENDER_READY_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [resetKey]);

  useEffect(() => {
    return () => {
      resetReportPreviewReadySignal();
    };
  }, []);

  const value = useMemo(
    () => ({
      registerBlock,
      unregisterBlock,
      markBlockReady,
      isReady,
    }),
    [registerBlock, unregisterBlock, markBlockReady, isReady],
  );

  return (
    <ReportPreviewRenderReadyContext.Provider value={value}>
      {children}
    </ReportPreviewRenderReadyContext.Provider>
  );
};

export const ReportPreviewRenderReadySignal: FC = () => {
  const context = useContext(ReportPreviewRenderReadyContext);

  useEffect(() => {
    if (context?.isReady) {
      signalReportPreviewReady();
    }
  }, [context?.isReady]);

  return null;
};

interface UseReportPreviewRenderReadyBlockOptions {
  immediateReady?: boolean;
  enabled?: boolean;
}

export function useReportPreviewRenderReadyBlock(
  blockId: string,
  options?: UseReportPreviewRenderReadyBlockOptions,
) {
  const context = useContext(ReportPreviewRenderReadyContext);
  const immediateReady = options?.immediateReady ?? false;
  const enabled = options?.enabled ?? true;

  useLayoutEffect(() => {
    if (!context || !enabled) {
      return;
    }

    context.registerBlock(blockId);

    if (immediateReady) {
      context.markBlockReady(blockId);
    }

    return () => {
      context.unregisterBlock(blockId);
    };
  }, [blockId, context, enabled, immediateReady]);

  const markReady = useCallback(() => {
    context?.markBlockReady(blockId);
  }, [blockId, context]);

  return {
    isTracking: context !== null,
    markReady,
  };
}
